import React, { useCallback, useEffect, useRef, useState } from "react";

import Menu from "antd/es/menu";
import { defineMessages, useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { Territory } from "@mapmycustomers/shared/types/entity";
import { TerritoryDetail } from "@mapmycustomers/shared/types/entity/Territory";

import { useMap } from "@app/component/map/FeaturedMap";
import MapContextMenu from "@app/scene/map/components/MapContextMenu";
import ChangeVertexFunction from "@app/scene/map/components/TerritoriesMode/types/ChangeVertexFunction";
import { Paths } from "@app/scene/map/components/TerritoriesMode/types/EditBoundsCommand";
import anyColorToHex from "@app/util/colors/anyColorToHex";

type PolyMouseEvent = google.maps.PolyMouseEvent;
type LatLngLiteral = google.maps.LatLngLiteral;
type Path = google.maps.MVCArray<google.maps.LatLng>;

const messages = defineMessages({
  remove: {
    id: "map.territories.sidebar.editDrawer.polygon.contextMenu.remove",
    defaultMessage: "Remove",
    description:
      "Remove menu item of Territory's polygon in Edit Territory drawer in Territory Mode",
  },
});

interface Props {
  onChangeVertex?: ChangeVertexFunction;
  onMoveTerritory?: (newBounds: Paths) => void;
  // only needs some fields from Territory entity, not all
  territory: Pick<Territory, "color"> & {
    territoryDetail: Pick<TerritoryDetail, "opacity" | "points">;
  };
}

const TerritoryPolygon: React.FC<Props> = ({ onChangeVertex, onMoveTerritory, territory }) => {
  const intl = useIntl();
  const { map } = useMap();
  const polygon = useRef(new google.maps.Polygon({ draggable: true, editable: true }));

  const [contextMenu, setContextMenu] = useState<
    { pathIndex: number; position: LatLngLiteral; vertexIndex: number } | undefined
  >();

  // show/hide effect
  useEffect(() => {
    const poly = polygon.current;
    poly.setMap(map);
    return () => {
      poly.setMap(null);
    };
  }, [map]);

  // look'n'feel effect
  useEffect(() => {
    const color = anyColorToHex(territory.color ?? Color.BLACK);
    polygon.current.setOptions({
      fillColor: color,
      fillOpacity: territory.territoryDetail.opacity,
      strokeColor: color,
      strokeOpacity: 1,
      strokeWeight: 2,
    });
  }, [territory.color, territory.territoryDetail.opacity]);

  const sendUpdates = useRef<boolean>(true);

  // bounds & events effect
  useEffect(() => {
    const paths = territory.territoryDetail.points.coordinates.map((path) =>
      path.map(([lng, lat]) => ({ lat, lng }))
    );
    polygon.current.setPaths(paths);

    const listeners: google.maps.MapsEventListener[] = [];
    polygon.current.getPaths().forEach((path, pathIndex) => {
      listeners.push(
        google.maps.event.addListener(path, "insert_at", function (this: Path, index: number) {
          if (sendUpdates.current) {
            onChangeVertex?.("add", pathIndex, index, this.getAt(index).toJSON());
          }
        })
      );
      listeners.push(
        google.maps.event.addListener(path, "set_at", function (this: Path, index: number) {
          if (sendUpdates.current) {
            onChangeVertex?.("edit", pathIndex, index, this.getAt(index).toJSON());
          }
        })
      );
      listeners.push(
        google.maps.event.addListener(path, "remove_at", function (this: Path, index: number) {
          if (sendUpdates.current) {
            onChangeVertex?.("remove", pathIndex, index);
          }
        })
      );
    });

    listeners.push(
      google.maps.event.addListener(polygon.current, "contextmenu", function (e: PolyMouseEvent) {
        if (e.latLng && e.path !== undefined && e.vertex !== undefined) {
          setContextMenu({ pathIndex: e.path, position: e.latLng.toJSON(), vertexIndex: e.vertex });
        }
      })
    );
    listeners.push(
      google.maps.event.addListener(polygon.current, "dragstart", function () {
        sendUpdates.current = false; // prevent add/edit/remove events while dragging, otherwise it generates a ton of events
      })
    );
    listeners.push(
      google.maps.event.addListener(polygon.current, "dragend", function (this: any) {
        sendUpdates.current = true; // enable vertex changes reporting back again
        const paths: Paths = [];
        // convert google paths in LongLat[][]
        polygon.current.getPaths().forEach((path) => {
          const coordinates: LongLat[] = [];
          path.forEach((latLng) => {
            coordinates.push([latLng.lng(), latLng.lat()]);
          });
          paths.push(coordinates);
        });
        onMoveTerritory?.(paths);
      })
    );

    return () => {
      listeners.forEach((listener) => listener.remove());
    };
  }, [onChangeVertex, onMoveTerritory, territory.territoryDetail.points]);

  const hideMenu = useCallback(() => setContextMenu(undefined), [setContextMenu]);

  const handleRemoveVertex = useCallback(() => {
    if (contextMenu) {
      onChangeVertex?.("remove", contextMenu.pathIndex, contextMenu.vertexIndex);
      hideMenu();
    }
  }, [contextMenu, hideMenu, onChangeVertex]);

  return (
    <MapContextMenu onHide={hideMenu} showAt={contextMenu?.position}>
      <Menu.Item key="remove" onClick={handleRemoveVertex}>
        {intl.formatMessage(messages.remove)}
      </Menu.Item>
    </MapContextMenu>
  );
};

export default TerritoryPolygon;
